import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, Trash2, AlertCircle } from 'lucide-react';

// Generic archived-items panel, used within a workshop's Foresight and
// Writing tabs. The caller supplies the fetch/restore/purge functions so
// this same component works for both session types.
const ArchivedSessionsPanel = ({ workshopId, fetchArchived, onRestore, onPurge, itemLabel }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchArchived(workshopId);
      setItems(data);
    } catch (error) {
      console.error(`Failed to load archived ${itemLabel}s:`, error);
    } finally {
      setIsLoading(false);
    }
  }, [workshopId, fetchArchived, itemLabel]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (item) => {
    try {
      await onRestore(item.id);
      setItems(items.filter(i => i.id !== item.id));
    } catch (error) {
      alert('Failed to restore: ' + error.message);
    }
  };

  const handlePurge = async (item) => {
    const confirmed = window.confirm(
      `Permanently delete "${item.name}" right now?\n\nThis skips the remaining ${item.days_remaining} day(s) in the archive and cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await onPurge(item.id);
      setItems(items.filter(i => i.id !== item.id));
    } catch (error) {
      alert('Failed to purge: ' + error.message);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-400 text-sm">Loading…</div>;
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">No archived {itemLabel}s in this workshop.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map(item => (
        <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-start justify-between mb-1">
            <h4 className="font-semibold text-gray-900">{item.name}</h4>
            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
              item.days_remaining <= 3 ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-gray-100 text-gray-600'
            }`}>
              {item.days_remaining === 0 ? 'purging soon' : `${item.days_remaining} day${item.days_remaining !== 1 ? 's' : ''} left`}
            </span>
          </div>
          {item.description && <p className="text-sm text-gray-600 mb-3">{item.description}</p>}
          <div className="flex gap-2">
            <button onClick={() => handleRestore(item)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#01006A] text-white rounded-lg hover:bg-[#010059] text-sm font-medium">
              <RotateCcw className="w-3.5 h-3.5" />
              Restore
            </button>
            <button onClick={() => handlePurge(item)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-sm font-medium">
              <Trash2 className="w-3.5 h-3.5" />
              Delete Forever
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ArchivedSessionsPanel;
